import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { WalletReportFilterModel } from 'src/app/models/wallet/wallet-report-filter.model';
import { ClientService } from 'src/app/services/archive/client.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { EnterpriseService } from 'src/app/services/management/enterprise.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { WalletReportService } from 'src/app/services/wallet/wallet-report.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

@Component({
  selector: 'app-summary-account-statement',
  templateUrl: './summary-account-statement.component.html',
  styleUrls: ['./summary-account-statement.component.scss'],
  standalone: false
})
export class SummaryAccountStatementComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  branchId: number = 0;
  changeFilters: boolean = false;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  formWalletReportFilter: FormGroup = new FormGroup({});
  listBranch: any[] = [];
  listClient: any[] = [];
  listEnterprise: any[] = [];
  nameWindow: string = 'Informe de Estado de Cuenta Resumido';
  projectName = ProjectName.projectName;

  constructor(private branchService: BranchService,
    private clientService: ClientService,
    private enterpriseService: EnterpriseService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private walletReportService: WalletReportService) {
    this.validateForm();
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getFirstBranchByUserAuthenticatedAsync();
    await this.getAllLists();
    await this.getFirstEnterpriseByBranch(this.branchId);
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  changeBranch(branchId: number | null) {
    if (branchId != 0 && branchId != null) {
      this.branchId = branchId;
      this.formWalletReportFilter.controls["enterpriseId"].patchValue(0);
    } else {
      this.branchId = 0;
      this.formWalletReportFilter.controls["branchId"].patchValue(0);
      this.formWalletReportFilter.controls["enterpriseId"].patchValue(0);
    }
    this.changeFilter();
    this.getAllLists('enterprises');
  }

  async changeClient(clientId: number) {
    if (clientId === 0 || clientId === null) {
      this.formWalletReportFilter.controls["clientId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeEnterprise(enterpriseId: number | null) {
    if (enterpriseId === 0 || enterpriseId === null) {
      this.formWalletReportFilter.controls["enterpriseId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  async clear() {
    this.changeFilter();
    this.validateForm();
    await this.getFirstBranchByUserAuthenticatedAsync();
    await this.getAllLists('enterprises');
    this.getFirstEnterpriseByBranch(this.branchId);
  }

  async exportExcel() {
    if (this.changeFilters) {
      const modelWalletReportFilter = this.formWalletReportFilter.value as WalletReportFilterModel;
      try {
        const response = await firstValueFrom(this.walletReportService.exportWalletReportSummaryAccountStatement(modelWalletReportFilter));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Informe de Estado de Cuenta Resumido.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.'
      );
    }
  }

  async getAllLists(...listsToLoad: Array<'branches' | 'enterprises' | 'clients'>) {
    try {
      const lists = {
        branches: async () => {
          this.listBranch = await firstValueFrom(this.branchService.getAllBranchesForSelectAsync());
        },
        enterprises: async () => {
          this.listEnterprise = await firstValueFrom(this.enterpriseService.getEnterprisesByBranchIdForSelect(this.branchId));
        },
        clients: async () => {
          this.listClient = await firstValueFrom(this.clientService.getAllClientsForSelectAsync());
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.branches(),
          lists.enterprises(),
          lists.clients()
        ]);
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      this.branchId = (await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync())).branchId;
      this.formWalletReportFilter.controls["branchId"].patchValue(this.branchId);
    } catch (error) {
      console.error("Error al consultar la sucursal:", error);
    }
  }

  async getFirstEnterpriseByBranch(branchId: number) {
    try {
      const enterprise = await firstValueFrom(this.enterpriseService.getFirstEnterpriseByBranch(branchId));
      this.formWalletReportFilter.controls["enterpriseId"].patchValue(enterprise.enterpriseId);
    } catch (error) {
      console.error("Error al consultar la empresa:", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para imprimir.'
      );
    }
  }

  async send() {
    Object.keys(this.formWalletReportFilter.controls).forEach(key => {
      const control = this.formWalletReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formWalletReportFilter.valid) {
      const modelWalletReportFilter = this.formWalletReportFilter.value as WalletReportFilterModel;
      try {
        this.dataReport = await firstValueFrom(this.walletReportService.walletReportSummaryAccountStatement(modelWalletReportFilter));
        if (this.dataReport.data && this.dataReport.data.length != 0 && this.dataReport.data.length != null) {
          this.changeFilters = true;
        } else {
          this.changeFilters = false;
          this.toastHandlerService.showToast('info', 'No se encontraron datos.');
        }
      } catch (error) {
        console.error("Error al consultar el informe:", error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      branchId: {
        required: 'El campo Sucursal es obligatorio.',
        min: 'Debe seleccionar una Sucursal válida.'
      },
      clientId: {
        notNumber: 'Debe seleccionar un Cliente válido.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formWalletReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formWalletReportFilter = this.formBuilder.group({
      branchId: [0, [Validators.required, Validators.min(1)]],
      enterpriseId: [0],
      clientId: [0, [CustomValidators.numberOnlyValidator()]]
    });
  }

}
